import * as uiAnalyticsConsumer from '@servicenow/ui-analytics-consumer';
import {
	SPAN_ATTR_GQL_BATCH,
	SPAN_DOCUMENT_TRACE_PARENT,
	SPAN_KIND_CLIENT,
	SPAN_TRACE_PARENT,
	SPNA_ATTR_GQL_QUERY
} from '../constants';

const {createTraceParentHeader, trace} = uiAnalyticsConsumer;

/**
 * Adds a key value pair to the span attributes.
 * @param {Span} span The span to add the attribute to.
 * @param {string} key The key of the attribute.
 * @param {string} value The value of the attribute.
 */
function appendSpanAttribute(span, key, value) {
	if (!span) return;
	const current = span.attributes[key] || [];
	current.push(value);
	span.setAttribute(key, current);
}

/**
 * Starts batch tracing for GraphQL operations.
 * @param {Object} params The parameters for batch tracing.
 * @returns {Span} The GraphQL batch span.
 */
function startBatchTracing(params) {
	const {operation, maxBatchSize, batchInterval, gqlBatchSpan: existingGqlBatchSpan} = params;
	if (existingGqlBatchSpan) return existingGqlBatchSpan;

	const activeSpan = operation?.queryContext?.getActiveSpan();
	if (!activeSpan) return null;

	const spanAttributes = {
		'config.maxSize': maxBatchSize,
		'config.interval': `${batchInterval}ms`
	};

	const gqlBatchSpan = trace('seismic.gql.batch', {
		kind: SPAN_KIND_CLIENT,
		attributes: {...spanAttributes, tagName: 'GraphQL', componentId: 'batching'}
	});
	return gqlBatchSpan;
}

/**
 * Updates headers with span information.
 * @param {Object} params The parameters for updating headers.
 */
function updateHeadersWithSpan(params) {
	const {query, batch, headers = {}, operations, index, gqlBatchSpan, queryContext} = params;
	const activeSpan = queryContext?.getActiveSpan();
	if (!activeSpan) return;

	setSpanAttributes(activeSpan, query, batch);
	headers[SPAN_DOCUMENT_TRACE_PARENT] = activeSpan.documentTraceParent;

	const activeTraceParent = createTraceParentHeader(activeSpan.id, activeSpan.traceId);
	if (batch) {
		updateBatchHeaders(headers, operations, index, gqlBatchSpan, activeSpan, activeTraceParent);
	} else {
		headers[SPAN_TRACE_PARENT] = activeTraceParent;
	}
}

function setSpanAttributes(activeSpan, query, batch) {
	if (query && typeof query === 'string') {
		activeSpan.setAttribute(SPNA_ATTR_GQL_QUERY, query.substring(0, 50));
	}
	activeSpan.setAttribute(SPAN_ATTR_GQL_BATCH, batch);
}

function updateBatchHeaders(
	headers,
	operations,
	index,
	gqlBatchSpan,
	activeSpan,
	activeTraceParent
) {
	operations[index][SPAN_TRACE_PARENT] = activeTraceParent;
	operations[index][SPAN_DOCUMENT_TRACE_PARENT] = activeSpan.documentTraceParent;
	if (gqlBatchSpan) {
		headers[SPAN_TRACE_PARENT] = createTraceParentHeader(gqlBatchSpan.id, gqlBatchSpan.traceId);
	}
}

function crossLinkBatchSpan(gqlBatchSpan, activeSpan) {
	if (gqlBatchSpan && activeSpan) {
		//use span.addLink API when available.
		activeSpan.links.push({
			context: {
				spanId: gqlBatchSpan.id,
				traceId: gqlBatchSpan.traceId
			},
			attributes: {
				'link.type': 'gql.batch'
			}
		});
		gqlBatchSpan.links.push({
			context: {
				spanId: activeSpan.id,
				traceId: activeSpan.traceId
			},
			attributes: {
				'link.type': 'gql.batch.item'
			}
		});
	}
}

/*
 * Tracks the batch event for the GraphQL operations.
 * @param {Object} operation The operation object.
 * @param {Span} gqlBatchSpan The GraphQL batch span.
 * @param {Array} queue The queue of operations.
 */

function addEventToBatchSpan(operation, gqlBatchSpan, queue) {
	if (!operation || !gqlBatchSpan || !queue) return;

	const ts = Date.now();
	const eventDetails = {
		operationName: operation.operationName,
		queueSize: queue.length
	};

	gqlBatchSpan.addEvent('items.queue.add', eventDetails, ts);
	const activeSpan = operation.queryContext?.getActiveSpan();
	if (activeSpan) {
		activeSpan.addEvent('gql.batch.enqueue', eventDetails, ts);
	}
}

/**
 * Completes batch tracking for GraphQL operations.
 * @param {Span} gqlBatchSpan The GraphQL batch span.
 * @param {boolean} isBatched Indicates if the operations are batched.
 * @param {Array} operations The list of operations.
 * @returns {Span|null} The updated GraphQL batch span or null.
 */
function completeBatchTracking(gqlBatchSpan, isBatched, operations) {
	if (gqlBatchSpan && isBatched) {
		const batchEnd = Date.now();
		const queueSize = operations.length;
		const batchStart = (gqlBatchSpan.events || []).find(event => event.name === 'items.queue.add');
		const batchProcessingTime = batchStart ? batchEnd - batchStart.time : 0;

		gqlBatchSpan.addEvent('items.queue.send', {queueSize}, batchEnd);
		gqlBatchSpan.setAttribute('items.queue.size', queueSize);

		if (batchProcessingTime > 0) {
			gqlBatchSpan.setAttribute('gql.batch.queuing.delay', batchProcessingTime);
			operations.forEach(({queryContext}) => {
				const operationSpan = queryContext?.getActiveSpan();
				const gqlBatchQueueEvent = operationSpan?.events.find(
					event => event.name === 'gql.batch.enqueue'
				);
				if (gqlBatchQueueEvent) {
					operationSpan.setAttribute('gql.batch.queuing.delay', batchEnd - gqlBatchQueueEvent.time);
				}
			});
		}
		gqlBatchSpan.end();
	}
	return null;
}

export {
	appendSpanAttribute,
	startBatchTracing,
	updateHeadersWithSpan,
	completeBatchTracking,
	addEventToBatchSpan,
	crossLinkBatchSpan
};
